import { Pencil, Trash2 } from 'lucide-react';
import { Checkbox, CheckMark } from '@tes/ui';
import type { Selection } from '../../lib/useSelection';
import type { VariableDef } from '../../lib/types';

export function VariableTable({
  variables,
  sel,
  onEdit,
  onDelete,
}: {
  variables: VariableDef[];
  sel: Selection;
  onEdit: (v: VariableDef) => void;
  onDelete: (v: VariableDef) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-slate-400">
          <tr>
            <th className="w-10 rounded-tl-lg bg-slate-50 px-3 py-2">
              <Checkbox
                checked={sel.allSelected}
                indeterminate={sel.someSelected}
                onToggle={() => sel.setAll(!sel.allSelected)}
                label={sel.allSelected ? 'Clear selection' : 'Select all variables'}
              />
            </th>
            <th className="bg-slate-50 px-3 py-2">Key</th>
            <th className="bg-slate-50 px-3 py-2">Label</th>
            <th className="bg-slate-50 px-3 py-2">Type</th>
            <th className="bg-slate-50 px-3 py-2">Source</th>
            <th className="bg-slate-50 px-3 py-2">Required</th>
            <th className="bg-slate-50 px-3 py-2">Default</th>
            <th className="bg-slate-50 px-3 py-2">Used</th>
            <th className="rounded-tr-lg bg-slate-50 px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {variables.map((v) => (
            <tr key={v.id} className={sel.has(v.id) ? 'bg-brand-50' : undefined}>
              <td className="px-3 py-2">
                <Checkbox
                  checked={sel.has(v.id)}
                  onToggle={(o) => sel.toggle(v.id, o)}
                  label={`Select ${v.key}`}
                />
              </td>
              <td className="px-3 py-2 font-mono text-xs">{v.key}</td>
              <td className="px-3 py-2">{v.label}</td>
              <td className="px-3 py-2">
                {v.type === 'checkbox' ? (
                  <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-700">
                    <CheckMark size={10} color="currentColor" /> checkbox
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">text</span>
                )}
              </td>
              <td className="px-3 py-2">
                {v.source === 'asked' ? (
                  <span>
                    <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-700">
                      asked
                    </span>
                    {v.groupKey && (
                      <span className="ml-1 text-[11px] text-slate-400">in {v.groupKey}</span>
                    )}
                    {v.askWhenKey && (
                      <span className="ml-1 text-[11px] text-slate-400">
                        when {v.askWhenKey} = {v.askWhenValue}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">auto</span>
                )}
              </td>
              <td className="px-3 py-2">
                {v.required ? (
                  <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-600">
                    required
                  </span>
                ) : v.groupKey ? (
                  <span className="text-xs text-slate-400">via group</span>
                ) : (
                  <span className="text-xs text-slate-400">optional</span>
                )}
              </td>
              <td className="max-w-[10rem] truncate px-3 py-2 text-slate-500">
                {v.type === 'checkbox'
                  ? v.default === 'true'
                    ? 'ticked'
                    : 'unticked'
                  : v.default || '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">
                {v.usage?.templates
                  ? `${v.usage.placements}× in ${v.usage.templates} template(s)`
                  : '—'}
              </td>
              <td className="px-3 py-2">
                <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onEdit(v)}
                    className="rounded p-1 text-slate-500 hover:bg-slate-100"
                    aria-label={`Edit ${v.key}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(v)}
                    className="rounded p-1 text-red-500 hover:bg-red-50"
                    aria-label={`Delete ${v.key}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
